import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Phone, Mail, Instagram, Facebook, ExternalLink, Play, Send, Globe, TrendingUp, Users, Zap, ChevronRight, X, MessageCircle, ArrowRight, Sparkles, Target, BarChart3, Rocket } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_canvas-demo/artifacts/c288di76_28201.png';
const WHATSAPP_NUMBER = '13854246551';

// Particle Canvas Component
const ParticleCanvas = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const particlesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Initialize particles
    const particleCount = 80;
    particlesRef.current = Array.from({ length: particleCount }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      radius: Math.random() * 2 + 1,
    }));

    const animate = () => {
      ctx.fillStyle = 'rgba(5, 17, 20, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particlesRef.current.forEach((particle, i) => {
        // Mouse interaction
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < 150) {
          particle.vx += dx * 0.0002;
          particle.vy += dy * 0.0002;
        }

        particle.x += particle.vx;
        particle.y += particle.vy;

        // Bounce off edges
        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 242, 254, ${0.3 + Math.random() * 0.2})`;
        ctx.fill();

        // Draw connections
        particlesRef.current.slice(i + 1).forEach(other => {
          const d = Math.sqrt((particle.x - other.x) ** 2 + (particle.y - other.y) ** 2);
          if (d < 120) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = `rgba(255, 138, 61, ${0.15 * (1 - d / 120)})`;
            ctx.stroke();
          }
        });
      });

      requestAnimationFrame(animate);
    };
    animate();

    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />;
};

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, end, duration]);

  return <span ref={ref} className="font-mono">{prefix}{count.toLocaleString()}{suffix}</span>;
};

// Marquee Component
const Marquee = ({ children, speed = 30 }) => {
  return (
    <div className="overflow-hidden whitespace-nowrap">
      <motion.div
        className="inline-flex gap-8"
        animate={{ x: [0, -1000] }}
        transition={{ duration: speed, repeat: Infinity, ease: "linear" }}
      >
        {children}
        {children}
      </motion.div>
    </div>
  );
};

// Glowing Card Component
const GlowCard = ({ children, className = '', glowColor = 'orange' }) => {
  const colors = {
    orange: 'hover:shadow-[0_0_30px_rgba(255,138,61,0.3)]',
    cyan: 'hover:shadow-[0_0_30px_rgba(0,242,254,0.3)]',
  };
  
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -5 }}
      className={`relative backdrop-blur-xl bg-[#0a1f26]/80 border border-[#1a4d5c]/40 rounded-2xl overflow-hidden transition-all duration-500 ${colors[glowColor]} ${className}`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#00f2fe]/5 to-transparent pointer-events-none" />
      {children}
    </motion.div>
  );
};

export default function HomePage() {
  const [portfolio, setPortfolio] = useState([]);
  const [filteredPortfolio, setFilteredPortfolio] = useState([]);
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [selectedProject, setSelectedProject] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadSlider, setLeadSlider] = useState(50);
  const [showWhatsAppTooltip, setShowWhatsAppTooltip] = useState(false);
  const [visibleCount, setVisibleCount] = useState(12);
  
  const { scrollYProgress } = useScroll();
  const headerOpacity = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  const categories = ['Todos', 'Página Web', 'Video'];

  useEffect(() => {
    fetchPortfolio();
  }, []);

  useEffect(() => {
    if (activeFilter === 'Todos') {
      setFilteredPortfolio(portfolio);
    } else {
      setFilteredPortfolio(portfolio.filter(p => p.category === activeFilter));
    }
    // Reset visible count when filter changes
    setVisibleCount(12);
  }, [activeFilter, portfolio]);

  const fetchPortfolio = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portfolio?limit=100`);
      const data = await response.json();
      setPortfolio(data);
      setFilteredPortfolio(data);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm),
      });
      if (response.ok) {
        toast.success('¡Mensaje enviado! Te contactaremos pronto.');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        toast.error('Error al enviar el mensaje. Intenta de nuevo.');
      }
    } catch (error) {
      toast.error('Error de conexión. Intenta de nuevo.');
    }
    setIsSubmitting(false);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Calculate ROI based on slider
  const calculateROI = useCallback(() => {
    const currentLeads = leadSlider;
    const conversionRate = 0.35; // 35% conversion with our system
    const avgClientValue = 2500; // $2,500 average client value
    const newClients = Math.floor(currentLeads * conversionRate);
    const monthlyRevenue = newClients * avgClientValue;
    return { newClients, monthlyRevenue };
  }, [leadSlider]);

  const roi = calculateROI();

  return (
    <div className="min-h-screen bg-[#051114] text-[#f0f9fa] overflow-x-hidden" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <ParticleCanvas />
      
      {/* Glassmorphism Header */}
      <motion.header 
        style={{ opacity: headerOpacity }}
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#051114]/80 border-b border-[#1a4d5c]/30"
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <motion.img 
            src={LOGO_URL} 
            alt="De Cero a Clientes" 
            className="h-12 md:h-14 cursor-pointer"
            whileHover={{ scale: 1.05 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            data-testid="header-logo"
          />
          <nav className="hidden md:flex items-center gap-8">
            {['servicios', 'portafolio', 'calculadora', 'contacto'].map((item) => (
              <motion.button
                key={item}
                onClick={() => scrollToSection(item)}
                className="text-sm font-medium uppercase tracking-wider text-[#94a3b8] hover:text-[#00f2fe] transition-colors"
                whileHover={{ y: -2 }}
                data-testid={`nav-${item}`}
              >
                {item}
              </motion.button>
            ))}
          </nav>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection('contacto')}
            className="px-6 py-2.5 bg-gradient-to-r from-[#ff8a3d] to-[#ff9e5e] text-[#051114] font-bold text-sm rounded-full shadow-lg shadow-[#ff8a3d]/25"
            data-testid="header-cta"
          >
            Comenzar
          </motion.button>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Content */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 space-y-8"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#00f2fe]/10 border border-[#00f2fe]/30 rounded-full"
            >
              <Sparkles className="w-4 h-4 text-[#00f2fe]" />
              <span className="text-xs font-mono uppercase tracking-wider text-[#00f2fe]">Marketing Digital que Convierte</span>
            </motion.div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold uppercase leading-none" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <span className="text-[#f0f9fa]">Tu negocio</span>
              <br />
              <span className="text-[#f0f9fa]">no necesita más</span>
              <br />
              <motion.span 
                className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff8a3d] to-[#ff9e5e]"
                animate={{ 
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                Marketing
              </motion.span>
            </h1>

            <p className="text-xl md:text-2xl text-[#94a3b8] max-w-xl leading-relaxed">
              Necesita un <span className="text-[#00f2fe] font-semibold">sistema que genere</span>
              <br />
              <span className="text-4xl md:text-5xl font-bold text-[#ff8a3d] uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>CLIENTES</span>
            </p>

            <div className="flex flex-wrap gap-4">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(255,138,61,0.4)' }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('contacto')}
                className="px-8 py-4 bg-gradient-to-r from-[#ff8a3d] to-[#ff9e5e] text-[#051114] font-bold text-lg rounded-full flex items-center gap-2"
                data-testid="hero-cta-primary"
              >
                Comenzar Ahora <ArrowRight className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('portafolio')}
                className="px-8 py-4 border-2 border-[#00f2fe]/50 text-[#00f2fe] font-bold text-lg rounded-full hover:bg-[#00f2fe]/10 transition-colors"
                data-testid="hero-cta-secondary"
              >
                Ver Proyectos
              </motion.button>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-[#1a4d5c]/30">
              {[
                { value: 320, suffix: '+', label: 'Clientes Satisfechos' },
                { value: 98, suffix: '%', label: 'Tasa de Retención' },
                { value: 4.8, suffix: 'M', label: 'Revenue Generado', prefix: '$' },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  className="text-center"
                >
                  <div className="text-2xl md:text-3xl font-bold text-[#00f2fe]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    <AnimatedCounter end={stat.value} prefix={stat.prefix} suffix={stat.suffix} />
                  </div>
                  <div className="text-xs text-[#94a3b8] uppercase tracking-wider mt-1">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Content - Interactive Growth Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:col-span-5 relative"
          >
            <GlowCard className="p-8" glowColor="cyan">
              <div className="text-center space-y-6">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#ff8a3d] to-[#ff9e5e] rounded-2xl flex items-center justify-center">
                  <TrendingUp className="w-10 h-10 text-[#051114]" />
                </div>
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>Sistema de Crecimiento</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Globe, label: 'Páginas Web' },
                    { icon: Target, label: 'Campañas PPC' },
                    { icon: Users, label: 'Redes Sociales' },
                    { icon: Rocket, label: 'Ventas Remotas' },
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      whileHover={{ scale: 1.05 }}
                      className="p-4 bg-[#051114]/50 rounded-xl border border-[#1a4d5c]/30"
                    >
                      <item.icon className="w-6 h-6 text-[#00f2fe] mx-auto mb-2" />
                      <span className="text-xs text-[#94a3b8]">{item.label}</span>
                    </motion.div>
                  ))}
                </div>
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-6xl font-bold text-[#ff8a3d]"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  +35%
                </motion.div>
                <p className="text-sm text-[#94a3b8]">Incremento promedio en conversiones</p>
              </div>
            </GlowCard>

            {/* Floating badges */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute -top-4 -right-4 px-4 py-2 bg-[#ff8a3d] text-[#051114] text-xs font-bold rounded-full"
            >
              100% Garantizado
            </motion.div>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -bottom-4 -left-4 px-4 py-2 bg-[#00f2fe] text-[#051114] text-xs font-bold rounded-full"
            >
              ROI Comprobado
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-[#00f2fe]/50 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-[#00f2fe] rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Marquee Banner */}
      <div className="py-6 bg-gradient-to-r from-[#ff8a3d] to-[#ff9e5e] overflow-hidden">
        <Marquee speed={25}>
          {['Páginas Web', 'Marketing Digital', 'Campañas PPC', 'Redes Sociales', 'SEO', 'Diseño Gráfico', 'Videos Animados', 'Branding'].map((item, i) => (
            <span key={i} className="text-[#051114] font-bold text-lg uppercase tracking-wider flex items-center gap-4">
              {item} <Sparkles className="w-4 h-4" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* Services Section */}
      <section id="servicios" className="py-24 md:py-32 relative" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#00f2fe]">Nuestros Servicios</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4 uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Servicios de <span className="text-[#ff8a3d]">Alto Impacto</span>
            </h2>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Large Card - Páginas Web */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <GlowCard className="p-8 h-full">
                <div className="flex flex-col h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#ff8a3d] to-[#ff9e5e] rounded-2xl flex items-center justify-center mb-6">
                    <Globe className="w-8 h-8 text-[#051114]" />
                  </div>
                  <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>Páginas Web Profesionales</h3>
                  <p className="text-[#94a3b8] mb-6 flex-grow">Diseñamos y desarrollamos sitios web que convierten visitantes en clientes. Optimizados para SEO, velocidad y experiencia de usuario.</p>
                  <div className="grid grid-cols-2 gap-4">
                    {['Diseño Responsivo', 'SEO Optimizado', 'Alta Velocidad', 'CMS Fácil'].map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-[#00f2fe]">
                        <ChevronRight className="w-4 h-4" />
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              </GlowCard>
            </motion.div>

            {/* Small Card - Campañas PPC */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <GlowCard className="p-8 h-full" glowColor="cyan">
                <div className="w-14 h-14 bg-[#00f2fe]/20 rounded-xl flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-[#00f2fe]" />
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>Campañas PPC</h3>
                <p className="text-[#94a3b8] text-sm">Publicidad en Google y Meta que genera leads calificados con ROI comprobado.</p>
              </GlowCard>
            </motion.div>

            {/* Small Card - Redes Sociales */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <GlowCard className="p-8 h-full">
                <div className="w-14 h-14 bg-[#ff8a3d]/20 rounded-xl flex items-center justify-center mb-6">
                  <Instagram className="w-7 h-7 text-[#ff8a3d]" />
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>Redes Sociales</h3>
                <p className="text-[#94a3b8] text-sm">Gestión y publicidad en todas las plataformas principales para máximo alcance.</p>
              </GlowCard>
            </motion.div>

            {/* Large Card - Vendedor Remoto */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-2"
            >
              <GlowCard className="p-8 h-full" glowColor="cyan">
                <div className="flex flex-col md:flex-row items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#00f2fe] to-[#00d4e0] rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-8 h-8 text-[#051114]" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>Vendedor Experto Remoto</h3>
                    <p className="text-[#94a3b8]">Un profesional de ventas dedicado para cerrar más negocios y aumentar tu tasa de conversión. Nuestros vendedores están entrenados para convertir prospectos en clientes.</p>
                  </div>
                </div>
              </GlowCard>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section id="calculadora" className="py-24 bg-[#0a1f26]/50 relative" data-testid="calculator-section">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <GlowCard className="p-8 md:p-12">
              <div className="text-center mb-10">
                <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#00f2fe]">Calculadora de Crecimiento</span>
                <h2 className="text-3xl md:text-4xl font-bold mt-4 uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Calcula tu <span className="text-[#ff8a3d]">Potencial</span>
                </h2>
              </div>

              <div className="space-y-8">
                <div>
                  <label className="block text-sm text-[#94a3b8] mb-4">
                    ¿Cuántos leads/consultas recibes al mes?
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="500"
                    value={leadSlider}
                    onChange={(e) => setLeadSlider(Number(e.target.value))}
                    className="w-full h-3 bg-[#051114] rounded-full appearance-none cursor-pointer accent-[#ff8a3d]"
                    data-testid="roi-slider"
                  />
                  <div className="flex justify-between mt-2 text-sm text-[#94a3b8]">
                    <span>10</span>
                    <span className="text-2xl font-bold text-[#00f2fe]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{leadSlider}</span>
                    <span>500</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-6 bg-[#051114] rounded-xl border border-[#1a4d5c]/30 text-center">
                    <div className="text-sm text-[#94a3b8] mb-2">Nuevos Clientes/Mes</div>
                    <div className="text-5xl font-bold text-[#00f2fe]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      +{roi.newClients}
                    </div>
                  </div>
                  <div className="p-6 bg-[#051114] rounded-xl border border-[#ff8a3d]/30 text-center">
                    <div className="text-sm text-[#94a3b8] mb-2">Revenue Mensual Estimado</div>
                    <div className="text-5xl font-bold text-[#ff8a3d]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      ${roi.monthlyRevenue.toLocaleString()}
                    </div>
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => scrollToSection('contacto')}
                  className="w-full py-4 bg-gradient-to-r from-[#ff8a3d] to-[#ff9e5e] text-[#051114] font-bold text-lg rounded-full"
                  data-testid="roi-cta"
                >
                  Quiero estos resultados
                </motion.button>
              </div>
            </GlowCard>
          </motion.div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portafolio" className="py-24 md:py-32 relative" data-testid="portfolio-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#00f2fe]">Nuestro Trabajo</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4 uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Proyectos <span className="text-[#ff8a3d]">Realizados</span>
            </h2>
          </motion.div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((cat) => (
              <motion.button
                key={cat}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveFilter(cat)}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  activeFilter === cat
                    ? 'bg-gradient-to-r from-[#ff8a3d] to-[#ff9e5e] text-[#051114]'
                    : 'bg-[#0a1f26] border border-[#1a4d5c]/50 text-[#94a3b8] hover:border-[#00f2fe]/50'
                }`}
                data-testid={`filter-${cat.toLowerCase().replace(' ', '-')}`}
              >
                {cat}
              </motion.button>
            ))}
          </div>

          {/* Projects Grid */}
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filteredPortfolio.slice(0, visibleCount).map((project) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                >
                  <GlowCard className="overflow-hidden group cursor-pointer" onClick={() => setSelectedProject(project)}>
                    <div className="relative aspect-video overflow-hidden">
                      {project.image_url ? (
                        <img
                          src={project.image_url}
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#0a1f26] to-[#051114] flex items-center justify-center">
                          <Globe className="w-12 h-12 text-[#1a4d5c]" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#051114] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileHover={{ opacity: 1, y: 0 }}
                        className="absolute bottom-4 left-4 right-4"
                      >
                        <span className="text-xs font-mono uppercase tracking-wider text-[#00f2fe]">{project.category}</span>
                      </motion.div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-bold mb-2 group-hover:text-[#ff8a3d] transition-colors">{project.title}</h3>
                      <p className="text-sm text-[#94a3b8] line-clamp-2">{project.description}</p>
                      {project.project_url && (
                        <a
                          href={project.project_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-2 mt-4 text-[#00f2fe] text-sm font-medium hover:gap-3 transition-all"
                          data-testid={`project-link-${project.id}`}
                        >
                          Ver Proyecto <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </GlowCard>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {filteredPortfolio.length > visibleCount && (
            <div className="text-center mt-12">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setVisibleCount(prev => prev + 12)}
                className="px-8 py-4 border-2 border-[#ff8a3d]/50 text-[#ff8a3d] font-bold rounded-full hover:bg-[#ff8a3d]/10 transition-colors"
                data-testid="load-more-projects"
              >
                Ver más proyectos ({filteredPortfolio.length - visibleCount} más)
              </motion.button>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-24 md:py-32 bg-[#0a1f26]/30 relative" data-testid="contact-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left - Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#00f2fe]">Hablemos</span>
                <h2 className="text-4xl md:text-5xl font-bold mt-4 uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Comienza <span className="text-[#ff8a3d]">Hoy</span>
                </h2>
              </div>

              <p className="text-xl text-[#94a3b8]">
                ¿Listo para transformar tu negocio? Contáctanos y descubre cómo podemos ayudarte a conseguir más clientes.
              </p>

              <div className="space-y-4">
                <a href="mailto:christian@deceroaclientes.com" className="flex items-center gap-4 text-lg hover:text-[#00f2fe] transition-colors" data-testid="contact-email">
                  <div className="w-12 h-12 bg-[#00f2fe]/10 rounded-xl flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#00f2fe]" />
                  </div>
                  christian@deceroaclientes.com
                </a>
                <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 text-lg hover:text-[#ff8a3d] transition-colors" data-testid="contact-whatsapp">
                  <div className="w-12 h-12 bg-[#ff8a3d]/10 rounded-xl flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-[#ff8a3d]" />
                  </div>
                  WhatsApp Directo
                </a>
              </div>

              {/* Social Links */}
              <div className="flex gap-4">
                <a href="https://www.instagram.com/chilistapia" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#0a1f26] border border-[#1a4d5c]/50 rounded-xl flex items-center justify-center hover:border-[#ff8a3d]/50 hover:bg-[#ff8a3d]/10 transition-all" data-testid="social-instagram">
                  <Instagram className="w-5 h-5 text-[#ff8a3d]" />
                </a>
                <a href="https://www.facebook.com/share/19VJzMLr6k/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#0a1f26] border border-[#1a4d5c]/50 rounded-xl flex items-center justify-center hover:border-[#00f2fe]/50 hover:bg-[#00f2fe]/10 transition-all" data-testid="social-facebook">
                  <Facebook className="w-5 h-5 text-[#00f2fe]" />
                </a>
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <GlowCard className="p-8">
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm text-[#94a3b8] mb-2">Nombre</label>
                    <input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-[#051114] border border-[#1a4d5c]/50 rounded-xl text-[#f0f9fa] focus:border-[#00f2fe] focus:outline-none transition-colors"
                      placeholder="Tu nombre"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#94a3b8] mb-2">Email</label>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-[#051114] border border-[#1a4d5c]/50 rounded-xl text-[#f0f9fa] focus:border-[#00f2fe] focus:outline-none transition-colors"
                      placeholder="tu@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#94a3b8] mb-2">Mensaje</label>
                    <textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      rows={4}
                      className="w-full px-4 py-3 bg-[#051114] border border-[#1a4d5c]/50 rounded-xl text-[#f0f9fa] focus:border-[#00f2fe] focus:outline-none transition-colors resize-none"
                      placeholder="Cuéntanos sobre tu proyecto..."
                      data-testid="contact-message-input"
                    />
                  </div>
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-4 bg-gradient-to-r from-[#ff8a3d] to-[#ff9e5e] text-[#051114] font-bold text-lg rounded-full disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'} <Send className="w-5 h-5" />
                  </motion.button>
                </form>
              </GlowCard>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-[#1a4d5c]/30" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img src={LOGO_URL} alt="De Cero a Clientes" className="h-12" />
            <p className="text-sm text-[#94a3b8]">
              © 2025 De Cero a Clientes. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1 }}
      >
        <div className="relative">
          <AnimatePresence>
            {showWhatsAppTooltip && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="absolute right-full mr-4 top-1/2 -translate-y-1/2 px-4 py-2 bg-[#0a1f26] border border-[#1a4d5c]/50 rounded-lg whitespace-nowrap text-sm"
              >
                ¡Chatea con nosotros!
              </motion.div>
            )}
          </AnimatePresence>
          
          <motion.a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('¡Hola! Quiero llevar mi negocio De Cero a Clientes')}`}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onHoverStart={() => setShowWhatsAppTooltip(true)}
            onHoverEnd={() => setShowWhatsAppTooltip(false)}
            className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-bold rounded-full shadow-lg shadow-[#25D366]/30"
            data-testid="whatsapp-float-button"
          >
            <MessageCircle className="w-6 h-6" />
            <span className="hidden sm:inline">Asesoría Gratis</span>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute -top-1 -right-1 w-3 h-3 bg-[#ff8a3d] rounded-full"
            />
          </motion.a>
        </div>
      </motion.div>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#051114]/90 backdrop-blur-sm"
            onClick={() => setSelectedProject(null)}
            data-testid="project-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-2xl w-full bg-[#0a1f26] border border-[#1a4d5c]/50 rounded-2xl overflow-hidden"
              data-testid="project-modal"
            >
              {selectedProject.image_url && (
                <img
                  src={selectedProject.image_url}
                  alt={selectedProject.title}
                  className="w-full aspect-video object-cover"
                />
              )}
              <div className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="text-xs font-mono uppercase tracking-wider text-[#00f2fe]">{selectedProject.category}</span>
                    <h3 className="text-2xl font-bold mt-2" style={{ fontFamily: 'Oswald, sans-serif' }}>{selectedProject.title}</h3>
                  </div>
                  <button
                    onClick={() => setSelectedProject(null)}
                    className="p-2 hover:bg-[#051114] rounded-lg transition-colors"
                    data-testid="modal-close-button"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <p className="text-[#94a3b8] mb-6">{selectedProject.description}</p>
                {selectedProject.project_url && (
                  <a
                    href={selectedProject.project_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#ff8a3d] to-[#ff9e5e] text-[#051114] font-bold rounded-full"
                    data-testid="modal-project-link"
                  >
                    Visitar Sitio <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
